import { supabase, Database } from '../lib/supabase';

type Organization = Database['public']['Tables']['organizations']['Row'];
type Profile = Database['public']['Tables']['profiles']['Row'];
type SimCard = Database['public']['Tables']['sim_cards']['Row'];

export const supabaseService = {
  // Organization operations
  async createOrganization(name: string, type: string, initialBalance: number = 0) {
    const { data, error } = await supabase
      .from('organizations')
      .insert({
        name,
        type,
        wallet_balance: initialBalance,
        low_balance_threshold: 100, // Default threshold
      })
      .select()
      .single();

    if (error) throw error;
    return data as Organization;
  },

  async getOrganization(id: string) {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as Organization;
  },

  async updateOrganization(id: string, updates: Partial<Organization>) {
    const { data, error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Organization;
  },

  // Profile operations
  async createProfile(userId: string, email: string, role: Profile['role'], organizationId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .insert({
        id: userId,
        email,
        role,
        organization_id: organizationId,
      })
      .select()
      .single();

    if (error) throw error;
    return data as Profile;
  },

  async getProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data as Profile;
  },

  // SIM card operations
  async createSimCard(organizationId: string, iccid: string, msisdn?: string) {
    const { data, error } = await supabase
      .from('sim_cards')
      .insert({
        organization_id: organizationId,
        iccid,
        msisdn,
        status: 'active',
        auto_topup_enabled: false,
        balance: 0,
        balance_last_checked: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data as SimCard;
  },

  async getSimCards(organizationId: string) {
    const { data, error } = await supabase
      .from('sim_cards')
      .select('*')
      .eq('organization_id', organizationId);

    if (error) throw error;
    return data as SimCard[];
  },

  async updateSimCard(id: string, updates: Partial<SimCard>) {
    const { data, error } = await supabase
      .from('sim_cards')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as SimCard;
  },

  // Auth operations
  async signUp(email: string, password: string) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) throw error;
    return data;
  },

  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  // Get current session
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  },
}; 